import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import {
  ArrowLeft,
  Package,
  User,
  Phone,
  Home,
  FileText,
  Upload,
  Trash2,
  Download,
  Edit,
  Clock,
  CheckCircle2,
  Truck,
  Footprints,
  X,
  MessageCircle,
  CircleDollarSign,
  Coins,
  UserCircle,
  Users,
  Check,
  FileDown,
  Printer
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";

// Timeline component for order status
const StatusTimeline = ({ currentStatus, isPaid }) => {
  // Check if status is negative
  const isNegativeStatus = currentStatus === 'non_accolta' || currentStatus === 'respinto';
  
  if (isNegativeStatus) {
    // Show simplified status for rejected/not accepted
    return (
      <div className="w-full py-4">
        <div className="flex justify-center">
          <div className={`px-6 py-4 rounded-2xl ${
            currentStatus === 'non_accolta' 
              ? 'bg-gradient-to-r from-rose-500 to-pink-500' 
              : 'bg-gradient-to-r from-red-500 to-red-600'
          } text-white shadow-lg`}>
            <div className="flex items-center gap-3">
              <X className="w-8 h-8" />
              <div>
                <p className="text-lg font-bold">
                  {currentStatus === 'non_accolta' ? 'Richiesta Non Accolta' : 'Ordine Respinto'}
                </p>
                <p className="text-sm opacity-80">
                  {currentStatus === 'non_accolta' 
                    ? 'La richiesta non è stata accolta' 
                    : 'L\'ordine è stato respinto'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const steps = [
    { id: "richiesta", label: "Richiesta", icon: Clock, color: "amber" },
    { id: "pronto", label: "Pronto", icon: CheckCircle2, color: "blue" },
    { id: "consegnato", label: "Consegnato", icon: Truck, color: "emerald" }
  ];
  
  const getStepIndex = (status) => steps.findIndex(s => s.id === status);
  const currentIndex = getStepIndex(currentStatus);
  
  return (
    <div className="w-full py-4">
      <div className="flex items-center justify-between relative">
        {/* Progress line background */}
        <div className="absolute left-0 right-0 top-1/2 h-1 bg-slate-200 -translate-y-1/2 z-0" />
        
        {/* Progress line filled */}
        <div 
          className="absolute left-0 top-1/2 h-1 bg-gradient-to-r from-amber-500 via-blue-500 to-emerald-500 -translate-y-1/2 z-0 transition-all duration-700 ease-out"
          style={{ width: `${currentIndex === 0 ? 0 : currentIndex === 1 ? 50 : 100}%` }}
        />
        
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          const isCompleted = index <= currentIndex;
          const isCurrent = index === currentIndex;
          
          return (
            <div key={step.id} className="flex flex-col items-center z-10">
              <div 
                className={`
                  w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500
                  ${isCompleted 
                    ? `bg-${step.color}-500 text-white shadow-lg shadow-${step.color}-500/30` 
                    : 'bg-white border-2 border-slate-200 text-slate-400'
                  }
                  ${isCurrent ? 'ring-4 ring-offset-2 ring-' + step.color + '-200 scale-110' : ''}
                `}
                style={{
                  backgroundColor: isCompleted ? (
                    step.color === 'amber' ? '#f59e0b' : 
                    step.color === 'blue' ? '#3b82f6' : 
                    '#10b981'
                  ) : '#fff',
                  boxShadow: isCompleted ? `0 4px 14px ${
                    step.color === 'amber' ? 'rgba(245,158,11,0.3)' : 
                    step.color === 'blue' ? 'rgba(59,130,246,0.3)' : 
                    'rgba(16,185,129,0.3)'
                  }` : 'none'
                }}
              >
                {isCompleted ? (
                  index < currentIndex ? (
                    <Check className="w-6 h-6" />
                  ) : (
                    <StepIcon className="w-6 h-6" />
                  )
                ) : (
                  <StepIcon className="w-5 h-5" />
                )}
              </div>
              <span className={`mt-2 text-sm font-medium transition-colors ${
                isCompleted ? 'text-slate-900' : 'text-slate-400'
              }`}>
                {step.label}
              </span>
              {isCurrent && (
                <span className="text-xs text-slate-500 mt-0.5">Attuale</span>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Paid indicator */}
      {isPaid && (
        <div className="mt-4 flex justify-center">
          <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full border border-green-200 animate-pulse">
            <CircleDollarSign className="w-4 h-4" />
            <span className="text-sm font-medium">Ordine Pagato</span>
          </div>
        </div>
      )}
    </div>
  );
};

const OrderDetail = ({ user }) => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState("");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedCollaborator, setSelectedCollaborator] = useState("");
  const [assigning, setAssigning] = useState(false);
  
  const docTypes = [
    { value: "prescrizione", label: "Prescrizione Medica" },
    { value: "invalidita", label: "Pratiche Invalidità" },
    { value: "documento_identita", label: "Documento d'Identità" },
    { value: "tessera_sanitaria", label: "Tessera Sanitaria" },
    { value: "mandato", label: "Mandato" }
  ];
  
  useEffect(() => {
    fetchOrder();
    if (canReassign) {
      fetchCollaborators();
    }
  }, [orderId]);
  
  const fetchOrder = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
      setOrder(response.data);
    } catch (error) {
      toast.error("Ordine non trovato");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCollaborators = async () => {
    try {
      const response = await axios.get(`${API}/collaborators`, { withCredentials: true });
      setCollaborators(response.data);
    } catch (error) {
      console.error("Error fetching collaborators:", error);
    }
  };
  
  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { stato: newStatus }, { withCredentials: true });
      toast.success(`Stato aggiornato a "${newStatus}"`);
      fetchOrder();
    } catch (error) {
      toast.error("Errore nell'aggiornamento dello stato");
    }
  };
  
  const handleTogglePaid = async () => {
    if (!canSetPaid) {
      toast.error("Non hai i permessi per modificare lo stato pagamento");
      return;
    }
    
    try {
      await axios.put(`${API}/orders/${orderId}`, { pagato: !order?.pagato }, { withCredentials: true });
      toast.success(order?.pagato ? "Pagamento annullato" : "Ordine segnato come pagato");
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    }
  };
  
  const canDelete = user?.role === "admin";
  const canSetPaid = user?.role === "admin";  // Solo Admin può segnare come pagato
  const canReassign = user?.role === "admin" || user?.role === "titolare";
  const canEdit = user?.role === "admin" || user?.role === "titolare";
  
  const handleAssignCollaborator = async () => {
    if (!selectedCollaborator) {
      toast.error("Seleziona un collaboratore");
      return;
    }
    
    setAssigning(true);
    try {
      await axios.put(`${API}/orders/${orderId}/assign`, {
        collaborator_id: selectedCollaborator
      }, { withCredentials: true });
      
      toast.success("Ordine riassegnato");
      setAssignDialogOpen(false);
      setSelectedCollaborator("");
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella riassegnazione");
    } finally {
      setAssigning(false);
    }
  };
  
  const handleDownloadPDF = () => {
    const statusLabels = {
      richiesta: "Richiesta",
      pronto: "Pronto",
      consegnato: "Consegnato",
      non_accolta: "Non Accolta",
      respinto: "Respinto"
    };
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Ordine ${order?.order_id}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #1e293b; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 2px solid #e2e8f0; }
          .logo { font-size: 28px; font-weight: 700; color: #059669; }
          .order-id { font-size: 14px; color: #64748b; margin-top: 4px; }
          .status-badge { display: inline-block; padding: 8px 16px; border-radius: 20px; font-weight: 600; font-size: 14px; }
          .status-richiesta { background: #fef3c7; color: #92400e; }
          .status-pronto { background: #dbeafe; color: #1e40af; }
          .status-consegnato { background: #d1fae5; color: #065f46; }
          .status-non_accolta { background: #fce7f3; color: #9d174d; }
          .status-respinto { background: #fee2e2; color: #991b1b; }
          .section { margin-bottom: 30px; }
          .section-title { font-size: 12px; text-transform: uppercase; letter-spacing: 1px; color: #64748b; margin-bottom: 12px; font-weight: 600; }
          .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
          .info-item { background: #f8fafc; padding: 16px; border-radius: 8px; }
          .info-label { font-size: 12px; color: #64748b; margin-bottom: 4px; }
          .info-value { font-size: 16px; font-weight: 600; color: #1e293b; }
          .product-box { background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%); padding: 24px; border-radius: 12px; margin-top: 10px; }
          .product-name { font-size: 20px; font-weight: 700; color: #065f46; }
          .notes-box { background: #f1f5f9; padding: 16px; border-radius: 8px; border-left: 4px solid #3b82f6; margin-top: 10px; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; text-align: center; color: #94a3b8; font-size: 12px; }
          .paid-badge { background: #dcfce7; color: #166534; padding: 4px 12px; border-radius: 12px; font-size: 12px; margin-left: 10px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="logo">OrthoTrack</div>
            <div class="order-id">Ordine ${order?.order_id}</div>
            <div style="margin-top: 8px; color: #64748b; font-size: 13px;">
              Data: ${formatDate(order?.data_richiesta)}
            </div>
          </div>
          <div style="text-align: right;">
            <span class="status-badge status-${order?.stato}">${statusLabels[order?.stato] || order?.stato}</span>
            ${order?.pagato ? '<span class="paid-badge">✓ Pagato</span>' : ''}
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Dati Paziente</div>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Nome Completo</div>
              <div class="info-value">${order?.nome} ${order?.cognome}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Codice Fiscale</div>
              <div class="info-value">${order?.codice_fiscale || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Telefono</div>
              <div class="info-value">${order?.telefono || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Casa di Appartenenza</div>
              <div class="info-value">${order?.casa_appartenenza || '-'}</div>
            </div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Prodotto Richiesto</div>
          <div class="product-box">
            <div class="product-name">${order?.prodotto_richiesto}</div>
          </div>
        </div>
        
        ${order?.note ? `
          <div class="section">
            <div class="section-title">Note</div>
            <div class="notes-box">${order.note}</div>
          </div>
        ` : ''}
        
        ${order?.created_by_name ? `
          <div class="section">
            <div class="section-title">Informazioni Aggiuntive</div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Creato da</div>
                <div class="info-value">${order.created_by_name}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Documenti Allegati</div>
                <div class="info-value">${order?.documents?.length || 0} file</div>
              </div>
            </div>
          </div>
        ` : ''}
        
        <div class="footer">
          <p>Documento generato da OrthoTrack • ${new Date().toLocaleDateString('it-IT')} ${new Date().toLocaleTimeString('it-IT')}</p>
        </div>
        
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };
  
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedDocType) return;
    
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", selectedDocType);
    
    try {
      await axios.post(`${API}/orders/${orderId}/documents`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Documento caricato");
      setUploadDialogOpen(false);
      setSelectedDocType("");
      fetchOrder();
    } catch (error) {
      if (error.response?.data?.detail?.includes("Massimo 5")) {
        toast.error("Massimo 5 documenti per ordine");
      } else {
        toast.error("Errore nel caricamento");
      }
    } finally {
      setUploadingDoc(false);
    }
  };
  
  const handleDownloadDoc = async (doc) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/documents/${doc.doc_id}`, {
        withCredentials: true
      });
      
      // Convert base64 to blob
      const byteCharacters = atob(response.data.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: response.data.content_type });
      
      // Download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = response.data.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Errore nel download");
    }
  };
  
  const handleDeleteDoc = async (docId) => {
    if (!confirm("Eliminare questo documento?")) return;
    
    try {
      await axios.delete(`${API}/orders/${orderId}/documents/${docId}`, { withCredentials: true });
      toast.success("Documento eliminato");
      fetchOrder();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };
  
  const handleDeleteOrder = async () => {
    if (!confirm("Eliminare questo ordine?")) return;
    
    try {
      await axios.delete(`${API}/orders/${orderId}`, { withCredentials: true });
      toast.success("Ordine eliminato");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };
  
  // WhatsApp notification
  const handleWhatsAppNotification = () => {
    if (!order?.telefono) {
      toast.error("Numero di telefono non disponibile");
      return;
    }
    
    // Clean phone number (remove spaces, dashes, etc.)
    let phone = order.telefono.replace(/[\s\-\(\)]/g, "");
    // Remove leading + if present and ensure it starts with country code
    if (phone.startsWith("+")) {
      phone = phone.substring(1);
    } else if (phone.startsWith("0")) {
      // Italian number without country code
      phone = "39" + phone.substring(1);
    } else if (!phone.startsWith("39")) {
      phone = "39" + phone;
    }
    
    // Create message based on current status
    const statusMessages = {
      richiesta: `Gentile ${order.nome} ${order.cognome}, la informiamo che la sua richiesta per "${order.prodotto_richiesto}" è stata registrata con codice ${order.order_id}. La contatteremo appena sarà pronto.`,
      pronto: `Gentile ${order.nome} ${order.cognome}, il suo ordine "${order.prodotto_richiesto}" (${order.order_id}) è PRONTO per il ritiro. La aspettiamo!`,
      consegnato: `Gentile ${order.nome} ${order.cognome}, confermiamo la consegna del suo ordine "${order.prodotto_richiesto}" (${order.order_id}). Grazie per la fiducia!`
    };
    
    const message = statusMessages[order.stato] || statusMessages.richiesta;
    const encodedMessage = encodeURIComponent(message);
    
    // Open WhatsApp
    window.open(`https://wa.me/${phone}?text=${encodedMessage}`, "_blank");
  };
  
  const getStatusBadge = (stato) => {
    const styles = {
      richiesta: "bg-amber-50 text-amber-700 border-amber-200",
      pronto: "bg-blue-50 text-blue-700 border-blue-200",
      consegnato: "bg-emerald-50 text-emerald-700 border-emerald-200"
    };
    const labels = {
      richiesta: "Richiesta",
      pronto: "Pronto",
      consegnato: "Consegnato"
    };
    return (
      <Badge variant="outline" className={`${styles[stato]} border font-medium`}>
        {labels[stato]}
      </Badge>
    );
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };
  
  const getDocTypeLabel = (docType) => {
    return docTypes.find(d => d.value === docType)?.label || docType;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="glass-header sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16 gap-4">
              <Skeleton className="h-10 w-10 rounded-lg" />
              <Skeleton className="h-6 w-48" />
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-96 w-full rounded-xl" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="order-detail">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {order?.cognome} {order?.nome}
                </h1>
                <p className="text-xs text-slate-500 font-mono">{order?.order_id}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* PDF Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPDF}
                className="text-blue-600 hover:bg-blue-50"
                data-testid="download-pdf-btn"
              >
                <FileDown className="w-4 h-4 mr-2" />
                PDF
              </Button>
              
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/orders/${orderId}/edit`)}
                  data-testid="edit-order-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Modifica
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  onClick={handleDeleteOrder}
                  data-testid="delete-order-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Status Timeline Card */}
        <div className="card-hover p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Stato Ordine</h2>
            
            {/* Controls for Admin/Titolare */}
            {canEdit && (
              <div className="flex items-center gap-2">
                <Select value={order?.stato} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-44" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="richiesta">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-500" />
                        Richiesta
                      </div>
                    </SelectItem>
                    <SelectItem value="pronto">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-500" />
                        Pronto
                      </div>
                    </SelectItem>
                    <SelectItem value="consegnato">
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-emerald-500" />
                        Consegnato
                      </div>
                    </SelectItem>
                    <SelectItem value="non_accolta">
                      <div className="flex items-center gap-2">
                        <X className="w-4 h-4 text-rose-500" />
                        Non Accolta
                      </div>
                    </SelectItem>
                    <SelectItem value="respinto">
                      <div className="flex items-center gap-2">
                        <X className="w-4 h-4 text-red-600" />
                        Respinto
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTogglePaid}
                  className={order?.pagato 
                    ? "bg-green-100 border-green-300 text-green-700 hover:bg-green-200" 
                    : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                  }
                  data-testid="paid-btn"
                >
                  <CircleDollarSign className="w-4 h-4 mr-2" />
                  {order?.pagato ? "Pagato ✓" : "Segna Pagato"}
                </Button>
              </div>
            )}
          </div>
          
          {/* Visual Timeline */}
          <StatusTimeline currentStatus={order?.stato} isPaid={order?.pagato} />
          
          {/* Quick Actions - Cambio stato rapido */}
          {canEdit && order?.stato !== 'non_accolta' && order?.stato !== 'respinto' && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-xs text-slate-500 mb-2 text-center">Azioni Rapide</p>
              <div className="flex flex-wrap justify-center gap-2">
                {order?.stato === 'richiesta' && (
                  <>
                    <Button
                      size="sm"
                      className="bg-blue-500 hover:bg-blue-600 text-white gap-1.5"
                      onClick={() => handleStatusChange('pronto')}
                      data-testid="quick-pronto"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Segna Pronto
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-rose-200 text-rose-600 hover:bg-rose-50 gap-1.5"
                      onClick={() => handleStatusChange('non_accolta')}
                      data-testid="quick-non-accolta"
                    >
                      <X className="w-4 h-4" />
                      Non Accolta
                    </Button>
                  </>
                )}
                {order?.stato === 'pronto' && (
                  <>
                    <Button
                      size="sm"
                      className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5"
                      onClick={() => handleStatusChange('consegnato')}
                      data-testid="quick-consegnato"
                    >
                      <Truck className="w-4 h-4" />
                      Segna Consegnato
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-200 text-amber-600 hover:bg-amber-50 gap-1.5"
                      onClick={() => handleStatusChange('richiesta')}
                      data-testid="quick-richiesta"
                    >
                      <Clock className="w-4 h-4" />
                      Torna a Richiesta
                    </Button>
                  </>
                )}
                {order?.stato === 'consegnato' && !order?.pagato && (
                  <Button
                    size="sm"
                    className="bg-green-500 hover:bg-green-600 text-white gap-1.5"
                    onClick={handleTogglePaid}
                    data-testid="quick-pagato"
                  >
                    <CircleDollarSign className="w-4 h-4" />
                    Segna Pagato
                  </Button>
                )}
              </div>
            </div>
          )}
          
          {/* WhatsApp Button */}
          <div className="mt-4 pt-4 border-t border-slate-100 flex justify-center">
            <Button
              variant="outline"
              onClick={handleWhatsAppNotification}
              className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
              data-testid="whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Invia Aggiornamento WhatsApp
            </Button>
          </div>
        </div>
        
        {/* Patient Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card-hover p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="w-4 h-4" />
              Dati Paziente
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-slate-500">Nome Completo</p>
                <p className="font-medium text-slate-900">{order?.nome} {order?.cognome}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Codice Fiscale</p>
                <p className="font-mono text-sm text-slate-700">{order?.codice_fiscale}</p>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-400" />
                <a 
                  href={`tel:${order?.telefono}`} 
                  className="text-sm text-blue-600 hover:underline"
                >
                  {order?.telefono}
                </a>
                {/* WhatsApp quick button */}
                <button
                  onClick={handleWhatsAppNotification}
                  className="p-1.5 rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
                  title="Apri WhatsApp"
                  data-testid="whatsapp-phone-btn"
                >
                  <MessageCircle className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <Home className="w-4 h-4 text-slate-400" />
                <p className="text-sm text-slate-700">{order?.casa_appartenenza}</p>
              </div>
            </div>
          </div>
          
          <div className="card-hover p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Footprints className="w-4 h-4" />
              Prodotto Richiesto
            </h3>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="font-medium text-slate-900">{order?.prodotto_richiesto}</p>
              {order?.prezzo > 0 && (
                <p className="text-sm text-slate-500 mt-2 flex items-center gap-1">
                  <Coins className="w-4 h-4" />
                  {order.prezzo} Ortho
                </p>
              )}
            </div>
            
            {order?.created_by_name && (
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <UserCircle className="w-4 h-4" />
                  Creato da: <span className="font-medium text-slate-700">{order.created_by_name}</span>
                </div>
                
                {canReassign && (
                  <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="reassign-btn">
                        <Users className="w-4 h-4 mr-2" />
                        Riassegna
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Riassegna Ordine</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div>
                          <Label>Nuovo Collaboratore</Label>
                          <Select value={selectedCollaborator} onValueChange={setSelectedCollaborator}>
                            <SelectTrigger className="mt-1.5" data-testid="collaborator-select">
                              <SelectValue placeholder="Seleziona collaboratore..." />
                            </SelectTrigger>
                            <SelectContent>
                              {collaborators.map((collab) => (
                                <SelectItem key={collab.user_id} value={collab.user_id}>
                                  <div className="flex items-center gap-2">
                                    <span>{collab.name}</span>
                                    <Badge variant="outline" className="text-xs">
                                      {collab.role === "admin" ? "Admin" : collab.role === "titolare" ? "Titolare" : "Collaboratore"}
                                    </Badge>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="flex justify-end gap-3 pt-4">
                          <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
                            Annulla
                          </Button>
                          <Button
                            onClick={handleAssignCollaborator}
                            disabled={assigning || !selectedCollaborator}
                            className="bg-slate-900 hover:bg-slate-800"
                            data-testid="confirm-reassign-btn"
                          >
                            {assigning ? "Riassegnazione..." : "Riassegna"}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            )}
            
            {order?.note && (
              <div className="mt-4">
                <p className="text-xs text-slate-500 mb-2">Note</p>
                <p className="text-sm text-slate-600">{order?.note}</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Documents */}
        <div className="card-hover p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Documenti Allegati ({order?.documenti?.length || 0}/5)
            </h3>
            
            {(order?.documenti?.length || 0) < 5 && (
              <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="upload-doc-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    Carica Documento
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Carica Documento</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label>Tipo Documento</Label>
                      <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                        <SelectTrigger className="mt-1.5" data-testid="doc-type-select">
                          <SelectValue placeholder="Seleziona tipo..." />
                        </SelectTrigger>
                        <SelectContent>
                          {docTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>File</Label>
                      <Input
                        type="file"
                        accept="application/pdf"
                        onChange={handleFileUpload}
                        disabled={!selectedDocType || uploadingDoc}
                        className="mt-1.5"
                        data-testid="file-input"
                      />
                    </div>
                    
                    {uploadingDoc && (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
                        Caricamento in corso...
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
          
          {order?.documenti?.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p>Nessun documento caricato</p>
            </div>
          ) : (
            <div className="space-y-2">
              {order?.documenti?.map((doc) => (
                <div
                  key={doc.doc_id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  data-testid={`doc-${doc.doc_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-slate-200">
                      <FileText className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{getDocTypeLabel(doc.doc_type)}</p>
                      <p className="text-xs text-slate-500">{doc.filename}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownloadDoc(doc)}
                      data-testid={`download-doc-${doc.doc_id}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteDoc(doc.doc_id)}
                      data-testid={`delete-doc-${doc.doc_id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default OrderDetail;
